import React, { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { narrativeForActionType } from "@/lib/activityLogNarratives";

const NARRATIVE_KEY = "descricao_do_tipo_de_acao";

const DETAIL_KEY_LABELS_PT = {
  areas_alteradas: "Áreas do edital tocadas nesta gravação",
  campos_alterados: "Campos atualizados",
  coluna_kanban_anterior: "Coluna no Kanban (antes)",
  coluna_kanban_nova: "Coluna no Kanban (depois)",
  old_column_id: "ID da coluna anterior (Kanban)",
  new_column_id: "ID da coluna nova (Kanban)",
  mensagem: "Informação adicional",
  reason: "Motivo",
};

const LOGIN_REASON_LABELS = {
  invalid_password: "Senha incorreta",
};

/** Aceita details como objeto, array, string ou JSON em string. */
function normalizeDetails(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return null;
    try {
      const parsed = JSON.parse(t);
      if (parsed != null && typeof parsed === "object" && !Array.isArray(parsed) && Object.keys(parsed).length === 0) {
        return null;
      }
      return parsed;
    } catch {
      return raw;
    }
  }
  if (typeof raw === "object") {
    if (!Array.isArray(raw) && Object.keys(raw).length === 0) return null;
    return raw;
  }
  return raw;
}

function splitNarrativeAndRest(normalized) {
  if (!normalized || typeof normalized !== "object" || Array.isArray(normalized)) {
    return { narrativeText: null, rest: normalized };
  }
  const { [NARRATIVE_KEY]: narr, ...rest } = normalized;
  const narrativeText = typeof narr === "string" && narr.trim() ? narr.trim() : null;
  const restKeys = Object.keys(rest);
  return {
    narrativeText,
    rest: restKeys.length ? rest : null,
  };
}

function formatDetailValue(key, value) {
  if (value == null) return "—";
  if (key === "reason" && typeof value === "string") {
    return LOGIN_REASON_LABELS[value] || value;
  }
  if (Array.isArray(value)) {
    if (value.every((x) => typeof x === "string" || typeof x === "number")) {
      return value.map(String).join("\n• ");
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function humanizeRestObject(rest) {
  if (!rest || typeof rest !== "object" || Array.isArray(rest)) return [];
  return Object.entries(rest).map(([k, v]) => {
    const label = DETAIL_KEY_LABELS_PT[k] || k.replace(/_/g, " ");
    if ((k === "areas_alteradas" || k === "campos_alterados") && Array.isArray(v)) {
      return { key: k, label, isList: true, items: v.map((x) => String(x)) };
    }
    return { key: k, label, isList: false, value: formatDetailValue(k, v) };
  });
}

function formatDetailsBlock(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function buildRecordSummaryLines(log, actionLabelText) {
  if (!log || typeof log !== "object") return [];
  const lines = [];
  const ts = log.timestamp;
  if (ts) {
    try {
      lines.push([
        "Data/Hora",
        format(new Date(ts), "dd/MM/yyyy HH:mm:ss", { locale: ptBR }),
      ]);
    } catch {
      lines.push(["Data/Hora", String(ts)]);
    }
  }
  const user =
    [log.user_full_name, log.user_email].filter(Boolean).join(" — ") ||
    log.user_email ||
    log.user_full_name;
  if (user) lines.push(["Usuário", user]);
  const action = actionLabelText || log.action_type || "—";
  lines.push(["Tipo de ação", action]);
  if (log.entity_type) lines.push(["Entidade", log.entity_type]);
  if (log.entity_name_description) lines.push(["Descrição", log.entity_name_description]);
  if (log.status) {
    lines.push([
      "Status",
      log.status === "success" ? "Sucesso" : log.status === "failed" ? "Falha" : String(log.status),
    ]);
  }
  if (log.ip_address) lines.push(["IP", log.ip_address]);
  if (log.source_system) lines.push(["Origem", log.source_system]);
  if (log.id) lines.push(["ID do registro", log.id]);
  return lines;
}

export default function DetailsModal({ open, onOpenChange, log, actionLabel, details: detailsProp }) {
  const details = detailsProp !== undefined ? detailsProp : log?.details;
  const normalized = useMemo(() => normalizeDetails(details), [details]);

  const { narrativeText: narrativeFromPayload, rest } = useMemo(
    () => splitNarrativeAndRest(normalized),
    [normalized]
  );

  const narrativeParagraph = useMemo(() => {
    if (narrativeFromPayload) return narrativeFromPayload;
    return narrativeForActionType(log?.action_type);
  }, [narrativeFromPayload, log?.action_type]);

  const humanRows = useMemo(() => humanizeRestObject(rest), [rest]);
  const hasHumanRows = humanRows.length > 0;
  const rawFallbackBlock = useMemo(() => {
    if (hasHumanRows) return "";
    if (rest != null && typeof rest === "object" && !Array.isArray(rest)) return "";
    if (rest != null) return formatDetailsBlock(rest);
    return "";
  }, [hasHumanRows, rest]);

  const summaryLines = useMemo(
    () => buildRecordSummaryLines(log, actionLabel),
    [log, actionLabel]
  );

  const fullCopyText = useMemo(() => {
    const parts = [];
    if (narrativeParagraph) {
      parts.push("=== Sobre esta ação ===\n" + narrativeParagraph);
    }
    if (summaryLines.length > 0) {
      parts.push(
        "=== Resumo do registro ===\n" +
          summaryLines.map(([k, v]) => `${k}: ${v}`).join("\n")
      );
    }
    if (hasHumanRows) {
      parts.push(
        "=== Detalhes complementares ===\n" +
          humanRows
            .map((r) =>
              r.isList ? `${r.label}:\n${r.items.map((x) => `  - ${x}`).join("\n")}` : `${r.label}: ${r.value}`
            )
            .join("\n")
      );
    } else if (rawFallbackBlock) {
      parts.push("=== Detalhes complementares ===\n" + rawFallbackBlock);
    }
    return parts.join("\n\n").trim();
  }, [narrativeParagraph, summaryLines, hasHumanRows, humanRows, rawFallbackBlock]);

  const hasSummary = summaryLines.length > 0;
  const hasComplementares = hasHumanRows || Boolean(rawFallbackBlock);
  const empty = !narrativeParagraph && !hasSummary && !hasComplementares;

  const handleCopy = () => {
    if (!fullCopyText) return;
    navigator.clipboard.writeText(fullCopyText).then(
      () => toast.success("Copiado para a área de transferência."),
      () => toast.error("Falha ao copiar.")
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Detalhes da ação</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto space-y-4">
          {narrativeParagraph && (
            <div className="rounded-lg border border-blue-100 bg-blue-50/80 p-4 text-sm text-slate-800 leading-relaxed">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-800 mb-2">
                O que esta ação representa
              </p>
              <p>{narrativeParagraph}</p>
            </div>
          )}

          {hasSummary && (
            <div className="rounded border bg-slate-50 p-3 text-sm">
              <p className="font-medium text-slate-800 mb-2">Resumo do registro</p>
              <dl className="space-y-1.5 text-slate-700">
                {summaryLines.map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</dt>
                    <dd className="mt-0.5 whitespace-pre-wrap break-words">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {hasHumanRows && (
            <div>
              <p className="text-sm font-medium text-slate-800 mb-2">Detalhes complementares</p>
              <div className="rounded border bg-slate-50 p-3 space-y-3 text-sm">
                {humanRows.map((row) => (
                  <div key={row.key}>
                    <dt className="text-xs font-medium text-slate-500">{row.label}</dt>
                    <dd className="mt-1 whitespace-pre-wrap break-words text-slate-800">
                      {row.isList ? (
                        <ul className="list-disc pl-5 space-y-0.5">
                          {row.items.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      ) : (
                        row.value
                      )}
                    </dd>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!hasHumanRows && rawFallbackBlock && (
            <div>
              <p className="text-sm font-medium text-slate-800 mb-2">Detalhes complementares</p>
              <div className="rounded border bg-slate-50 p-3 max-h-[36vh] overflow-auto">
                <pre className="text-xs whitespace-pre-wrap break-words font-mono">{rawFallbackBlock}</pre>
              </div>
            </div>
          )}

          {empty && (
            <p className="text-sm text-slate-600 text-center py-6">
              Nenhum detalhe disponível para este registro.
            </p>
          )}

          {hasSummary && !hasComplementares && narrativeParagraph && (
            <p className="text-xs text-slate-500">
              Não há metadados técnicos adicionais; a descrição acima e o resumo cobrem o registro.
            </p>
          )}
        </div>
        {fullCopyText && (
          <Button variant="outline" size="sm" onClick={handleCopy} className="self-end shrink-0">
            <Copy className="w-4 h-4 mr-2" />
            Copiar
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
